import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import IgnisIcon from '../components/icons/IgnisIcon';
import RiskDistributionBar from '../components/RiskDistributionBar';
import MunicipioMap, { type MapFire } from '../components/municipio/MunicipioMap';
import FreguesiaTable from '../components/municipio/FreguesiaTable';
import ReplayPanel from '../components/municipio/ReplayPanel';
import { useAuth } from '../contexts/AuthProvider';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useMunicipioZones } from '../hooks/useMunicipioZones';
import type { ViewMode } from '../components/map/CountryRiskLayer';
import { loadReplay, type ReplayDoc } from '../lib/replay';
import { bandKindOf, FLAGGED_SHARE_PCT, hasSeasonalPercentile } from '../lib/riskText';
import {
  RISK_PT,
  fmtArea,
  fmtDateTime,
  fmtInt,
  loadConcelhos,
  loadLiveFresh,
  monthName,
  type Concelho,
  type ConcelhoLive,
  type LiveDoc,
} from '../lib/concelhos';

// The municipal console: one operations view per concelho, parameterised by slug.
//
// It is national on day one because every per-concelho artifact already is (278/278);
// the only thing that is customer-specific is which Supabase zone rows are seeded. So
// "the VFX dashboard" and "the product" are the same build.
//
// Access: useMunicipioZones (briefing zones UNION nowcast zones, + nowcast_admin for the
// owner view). Rendering only; the RPCs and RLS are the boundary.

const CONTACT_EMAIL = 'institucional@tutela.land';
const MAILTO = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
  'Pedido de acesso à consola municipal IGNIS',
)}`;

const RCM_PT: Record<number, string> = {
  1: 'Reduzido',
  2: 'Moderado',
  3: 'Elevado',
  4: 'Muito elevado',
  5: 'Máximo',
};
const rcmLabel = (l: number | null | undefined) =>
  l != null && RCM_PT[l] ? RCM_PT[l] : 'indisponível';

const TREND_PT: Record<string, string> = {
  sobe: '▲ sobe',
  desce: '▼ desce',
  'estável': '◆ estável',
};

const DISCLAIMER =
  'Esta consola é apoio à decisão e não substitui o índice oficial de perigo de incêndio ' +
  'rural (IPMA/ICNF, art. 43.º do DL 82/2021) nem os avisos da ANEPC. As pontuações Ignis ' +
  'são relativas (ordenação de risco), não probabilidades absolutas de incêndio.';

const DEGRADED_NOTE =
  'A publicação dos dados meteorológicos ERA5 (Copernicus) está atrasada e o mapa está em ' +
  'modo sazonal de recurso. As pontuações de hoje têm menor fiabilidade que o habitual; ' +
  'confirme sempre o índice oficial IPMA/ICNF.';

const REFRESH_MS = 120_000;
const REPLAY_DATE = '2026-07-03';

interface NowcastFire extends MapFire {
  estado: string | null;
  freguesia: string | null;
  started_at: string | null;
  operacionais: number | null;
  meios_terrestres: number | null;
  meios_aereos: number | null;
  concelho_slug: string | null;
  zones_nearby: { slug: string; distance_km: number }[];
}

interface NowcastDoc {
  generated_at: string;
  n_active: number;
  fires: NowcastFire[];
}

type Tab = 'hoje' | 'fogos' | 'historico' | 'replay';

function Spinner() {
  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center bg-ink-950">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-ink-700 border-t-ember" />
    </div>
  );
}

function Restricted() {
  const { user } = useAuth();
  return (
    <div className="min-h-[calc(100vh-4rem)] bg-ink-950 text-mist">
      <div className="mx-auto max-w-3xl px-6 py-20">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-ember/30 bg-ember/10 px-4 py-1.5 font-mono text-sm font-semibold text-ember">
          <IgnisIcon className="h-4 w-4" />
          IGNIS · Consola municipal
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
          Consola municipal de risco
        </h1>
        <p className="mt-6 text-lg leading-relaxed text-mist-muted">
          Uma vista operacional por concelho: risco Ignis a 1 km e por freguesia, perigo
          oficial IPMA/ICNF, fogos ativos no concelho e até 20 km, e o histórico do
          concelho. Para GTF e serviços municipais de proteção civil.
        </p>
        <div className="mt-10 rounded-xl border border-ember/40 bg-ember/10 px-5 py-4">
          {user ? (
            <p className="text-sm leading-relaxed text-mist">
              A sua conta (<span className="font-mono text-ember">{user.email}</span>) não tem
              concelhos atribuídos.
            </p>
          ) : (
            <p className="text-sm leading-relaxed text-mist">
              O acesso requer uma conta autorizada.{' '}
              <Link to="/entrar" className="font-semibold text-ember hover:underline">
                Entrar
              </Link>
              .
            </p>
          )}
        </div>
        <div className="mt-10 rounded-xl border border-ink-700 bg-ink-900 p-6">
          <h2 className="font-display text-xl font-bold text-mist">Como pedir acesso</h2>
          <p className="mt-3 leading-relaxed text-mist-muted">
            O acesso é atribuído por entidade (todo o domínio de email institucional) ou a
            endereços específicos, e limitado aos concelhos contratados.
          </p>
          <a
            href={MAILTO}
            className="mt-5 inline-flex items-center gap-2.5 rounded-lg bg-ember px-6 py-3 font-semibold text-ink-950 transition-all hover:brightness-110"
          >
            Solicitar acesso
          </a>
        </div>
        <p className="mt-10 text-sm text-mist-muted">
          As páginas de{' '}
          <Link to="/ignis/risco" className="font-medium text-ember hover:brightness-110">
            risco por concelho
          </Link>{' '}
          são públicas.
        </p>
      </div>
    </div>
  );
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-ink-700 bg-ink-950 px-4 py-3">
      <div className="font-mono text-[11px] uppercase tracking-widest text-mist-faint">
        {label}
      </div>
      <div className="mt-1.5 text-sm text-mist">{children}</div>
    </div>
  );
}

function FireRow({ fire, slug }: { fire: NowcastFire; slug: string }) {
  const near = fire.zones_nearby.find((z) => z.slug === slug);
  const inside = fire.concelho_slug === slug;
  const active =
    fire.estado_agrupado === 'Em Curso' || fire.estado_agrupado === 'Em Despacho';
  return (
    <li className="rounded-lg border border-ink-700 bg-ink-950 p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-semibold text-mist">
          {fire.concelho_anepc ?? '?'}
          {fire.freguesia && (
            <span className="font-normal text-mist-muted"> · {fire.freguesia}</span>
          )}
        </span>
        <span
          className={`rounded px-2 py-0.5 font-mono text-[11px] ${
            active ? 'bg-ember/20 text-ember' : 'bg-ink-800 text-mist-faint'
          }`}
        >
          {fire.estado_agrupado ?? fire.estado ?? '?'}
        </span>
      </div>
      <div className="mt-1 font-mono text-xs text-mist-faint">
        {fire.natureza ?? 'ocorrência'} · nº {fire.numero}
        {fire.started_at && ` · início ${fire.started_at.slice(0, 16)}`}
      </div>
      <div className="mt-1 font-mono text-xs text-mist-muted">
        {inside ? 'dentro do concelho' : near ? `a ${near.distance_km.toFixed(1)} km` : ''}
        {fire.operacionais != null && ` · ${fire.operacionais} operacionais`}
        {fire.meios_terrestres != null && ` · ${fire.meios_terrestres} terrestres`}
        {fire.meios_aereos != null && ` · ${fire.meios_aereos} aéreos`}
      </div>
    </li>
  );
}

function Console({
  concelho,
  live,
  liveDoc,
  isAdmin,
  allConcelhos,
  zones,
}: {
  concelho: Concelho;
  live: ConcelhoLive | undefined;
  liveDoc: LiveDoc;
  isAdmin: boolean;
  allConcelhos: Concelho[];
  zones: string[];
}) {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('hoje');
  const [viewMode, setViewMode] = useState<ViewMode>('risk');
  const [cells, setCells] = useState<number[][] | null>(null);
  const [nowcast, setNowcast] = useState<NowcastDoc | null>(null);
  const [replay, setReplay] = useState<ReplayDoc | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    const cdn = import.meta.env.VITE_DATA_URL;
    if (!cdn) return;
    let cancelled = false;
    setCells(null);
    fetch(`${cdn}/concelho_cells/${concelho.slug}.json`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: { cells: number[][] }) => !cancelled && setCells(d.cells))
      .catch(() => !cancelled && setCells([]));
    return () => {
      cancelled = true;
    };
  }, [concelho.slug]);

  useEffect(() => {
    const cdn = import.meta.env.VITE_DATA_URL;
    if (!cdn) return;
    let cancelled = false;
    const pull = () =>
      fetch(`${cdn}/nowcast/active.json`, { cache: 'no-store' })
        .then((r) => (r.ok ? r.json() : null))
        .then((d: NowcastDoc | null) => !cancelled && d && setNowcast(d))
        .catch(() => undefined);
    pull();
    const t = setInterval(pull, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setReplay(null);
    loadReplay(REPLAY_DATE, concelho.slug).then((d) => !cancelled && setReplay(d));
    return () => {
      cancelled = true;
    };
  }, [concelho.slug]);

  const fires = useMemo(() => {
    if (!nowcast) return [];
    return nowcast.fires.filter(
      (f) =>
        f.concelho_slug === concelho.slug ||
        f.zones_nearby.some((z) => z.slug === concelho.slug),
    );
  }, [nowcast, concelho.slug]);

  const inside = fires.filter((f) => f.concelho_slug === concelho.slug);
  const nearby = fires.filter((f) => f.concelho_slug !== concelho.slug);
  const mapFires: MapFire[] = fires.map((f) => ({
    ...f,
    distance_km: f.zones_nearby.find((z) => z.slug === concelho.slug)?.distance_km ?? null,
  }));

  const matches =
    query.trim().length >= 2
      ? allConcelhos
          .filter((c) => c.nome.toLowerCase().includes(query.trim().toLowerCase()))
          .slice(0, 8)
      : [];

  const fh = concelho.fire_history;
  const d = live?.delta;
  const f24 = live?.forecast24h;

  const tabs: [Tab, string][] = [
    ['hoje', 'Hoje'],
    ['fogos', `Fogos${fires.length ? ` (${fires.length})` : ''}`],
    ['historico', 'Histórico'],
    ...(replay ? ([['replay', REPLAY_DATE.slice(8) + ' jul']] as [Tab, string][]) : []),
  ];

  const tabClass = (active: boolean) =>
    `rounded-lg px-3.5 py-1.5 font-mono text-xs transition-colors ${
      active ? 'bg-ember text-ink-950' : 'bg-ink-800 text-mist-muted hover:text-mist'
    }`;

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-ink-950 text-mist">
      <div className="mx-auto max-w-6xl px-6 py-12">
        <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-ember/30 bg-ember/10 px-4 py-1.5 font-mono text-sm font-semibold text-ember">
          <IgnisIcon className="h-4 w-4" />
          IGNIS · Consola municipal{isAdmin && ' · admin'}
        </div>

        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
              {concelho.nome}
            </h1>
            <p className="mt-1.5 font-mono text-sm text-mist-faint">
              distrito de {concelho.distrito} · {concelho.nuts3} · {concelho.n_cells} células
              de 1 km · {fmtInt(concelho.area_km2)} km²
            </p>
          </div>
          <div className="text-right font-mono text-xs text-mist-faint">
            {liveDoc.for_date} · {fmtDateTime(liveDoc.prediction_time)}
            <br />
            {liveDoc.model_version}
          </div>
        </div>

        {(zones.length > 1 || isAdmin) && (
          <div className="mt-5 flex flex-wrap items-center gap-2">
            {zones.map((s) => {
              const c = allConcelhos.find((x) => x.slug === s);
              if (!c) return null;
              return (
                <button
                  key={s}
                  onClick={() => navigate(`/ignis/municipio/${s}`)}
                  className={tabClass(s === concelho.slug)}
                >
                  {c.nome}
                </button>
              );
            })}
            {isAdmin && (
              <div className="relative">
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="procurar concelho…"
                  className="w-48 rounded-lg border border-ink-700 bg-ink-900 px-3 py-1.5 font-mono text-xs text-mist placeholder:text-mist-faint focus:border-ember focus:outline-none"
                />
                {matches.length > 0 && (
                  <ul className="absolute z-[1000] mt-1 w-64 overflow-hidden rounded-lg border border-ink-700 bg-ink-900 shadow-xl">
                    {matches.map((c) => (
                      <li key={c.slug}>
                        <button
                          onClick={() => {
                            setQuery('');
                            navigate(`/ignis/municipio/${c.slug}`);
                          }}
                          className="block w-full px-3 py-2 text-left font-mono text-xs text-mist-muted hover:bg-ink-800 hover:text-mist"
                        >
                          {c.nome}{' '}
                          <span className="text-mist-faint">({c.distrito})</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}

        {liveDoc.degraded && (
          <div className="mt-6 rounded-xl border border-amber-500/50 bg-amber-500/10 px-5 py-4 text-sm leading-relaxed text-mist">
            <span className="font-semibold">Atenção:</span> {DEGRADED_NOTE}
          </div>
        )}

        <div className="mt-7 flex flex-wrap gap-2">
          {tabs.map(([t, label]) => (
            <button key={t} onClick={() => setTab(t)} className={tabClass(tab === t)}>
              {label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === 'hoje' && (
            <div className="space-y-6">
              <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
                <div className="rounded-xl border border-ink-700 bg-ink-900 p-5">
                  <div className="mb-3 flex items-center justify-between">
                    <span className="font-mono text-xs uppercase tracking-widest text-mist-faint">
                      Mapa 1 km
                    </span>
                    {/* The anomaly view needs the legacy percentile; the daily path publishes none (W5). */}
                    {hasSeasonalPercentile(cells) && (
                      <div className="flex gap-1">
                        <button
                          className={tabClass(viewMode === 'risk')}
                          onClick={() => setViewMode('risk')}
                        >
                          Risco
                        </button>
                        <button
                          className={tabClass(viewMode === 'anomaly')}
                          onClick={() => setViewMode('anomaly')}
                        >
                          Anomalia
                        </button>
                      </div>
                    )}
                  </div>
                  {cells && cells.length > 0 ? (
                    <MunicipioMap
                      slug={concelho.slug}
                      bbox={concelho.bbox}
                      cells={cells}
                      viewMode={hasSeasonalPercentile(cells) ? viewMode : 'risk'}
                      fires={mapFires}
                    />
                  ) : (
                    <div className="flex h-[26rem] items-center justify-center rounded-lg border border-ink-700 text-sm text-mist-faint">
                      {cells ? 'Mapa indisponível.' : 'a carregar…'}
                    </div>
                  )}
                  {fires.length > 0 && (
                    <p className="mt-2 font-mono text-[11px] text-mist-faint">
                      ● fogos ativos (fonte oficial ANEPC/AGIF), no concelho e até 20 km
                    </p>
                  )}
                </div>

                <div className="space-y-3">
                  {live?.rcm && (
                    <Stat label="Perigo oficial IPMA/ICNF">
                      <span className="font-semibold">hoje {rcmLabel(live.rcm.d0)}</span> ·
                      amanhã {rcmLabel(live.rcm.d1)}
                      <div className="mt-1 text-xs text-mist-faint">
                        Uma classe para todo o concelho. É a referência legal.
                      </div>
                    </Stat>
                  )}
                  {live && (
                    <Stat
                      label={
                        bandKindOf(liveDoc.model_version) === 'share'
                          ? `Ignis · área do concelho nos ${FLAGGED_SHARE_PCT}% do país com maior risco`
                          : 'Ignis · área em Moderado+'
                      }
                    >
                      <span className="font-semibold">{live.mod_plus_pct}%</span>
                      {d && (
                        <span className={d.mod_plus_pct > 0 ? 'text-ember' : 'text-mist-faint'}>
                          {' '}
                          ({d.mod_plus_pct > 0 ? '▲ +' : d.mod_plus_pct < 0 ? '▼ ' : '◆ '}
                          {d.mod_plus_pct} pp vs ontem)
                        </span>
                      )}
                      <div className="mt-2">
                        <RiskDistributionBar risk={live.risk} total={live.n_cells} />
                      </div>
                    </Stat>
                  )}
                  {live?.anomaly_mean != null && (
                    <Stat label="Percentil sazonal (anomalia)">
                      médio <span className="font-semibold">{Math.round(live.anomaly_mean)}</span>{' '}
                      · máximo{' '}
                      <span className="font-semibold">{Math.round(live.anomaly_max ?? 0)}</span>{' '}
                      <span className="text-mist-faint">(0-100)</span>
                    </Stat>
                  )}
                  {f24 && (
                    <Stat label="Amanhã (24 h)">
                      <span className="font-semibold">{TREND_PT[f24.trend] ?? f24.trend}</span>{' '}
                      <span className="text-mist-faint">
                        (Moderado+ {f24.mod_plus_pct}%, dominante {RISK_PT[f24.dominant]})
                      </span>
                    </Stat>
                  )}
                  <Stat label="Fogos ativos agora">
                    <span className="font-semibold">{inside.length}</span> no concelho ·{' '}
                    <span className="font-semibold">{nearby.length}</span> até 20 km
                  </Stat>
                </div>
              </div>

              {live?.freguesias && live.freguesias.length > 0 && (
                <div className="rounded-xl border border-ink-700 bg-ink-900 p-5">
                  <div className="mb-1 font-mono text-xs uppercase tracking-widest text-mist-faint">
                    Por freguesia · {live.freguesias.length} freguesias
                  </div>
                  <p className="mb-4 text-xs text-mist-faint">
                    O índice oficial dá uma classe a todo o concelho. Isto mostra onde, dentro
                    dele, está o risco hoje.
                  </p>
                  <FreguesiaTable rows={live.freguesias} />
                </div>
              )}
            </div>
          )}

          {tab === 'fogos' && (
            <div className="space-y-6">
              <div className="rounded-xl border border-ink-700 bg-ink-900 p-5">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-display text-lg font-bold text-mist">
                    {inside.length} no concelho · {nearby.length} até 20 km
                  </span>
                  <span className="font-mono text-xs text-mist-faint">
                    fonte oficial ANEPC/AGIF
                    {nowcast && ` · ${fmtDateTime(nowcast.generated_at)}`}
                  </span>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-mist-muted">
                  O fogo que ameaça um concelho começa muitas vezes no vizinho. Por isso a
                  vista inclui um anel de 20 km.
                </p>
                {fires.length > 0 ? (
                  <ul className="mt-4 space-y-2">
                    {[...inside, ...nearby].map((f) => (
                      <li key={f.numero}>
                        <FireRow fire={f} slug={concelho.slug} />
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-4 text-sm text-mist-faint">
                    Sem ocorrências rurais ativas no concelho nem até 20 km.
                  </p>
                )}
              </div>
            </div>
          )}

          {tab === 'historico' && (
            <div className="grid gap-6 sm:grid-cols-2">
              {fh ? (
                <>
                  <Stat label={`Fogos registados desde ${fh.since_year}`}>
                    <span className="font-display text-2xl font-bold">
                      {fmtInt(fh.n_fires)}
                    </span>
                    <div className="mt-1 text-xs text-mist-faint">
                      fonte: ICNF (registos consolidados). A Proteção Civil conta ocorrências
                      de despacho, tipicamente superiores.
                    </div>
                  </Stat>
                  <Stat label="Área ardida acumulada">
                    <span className="font-display text-2xl font-bold">
                      {fmtArea(fh.area_ha)}
                    </span>
                    <div className="mt-1 text-xs text-mist-faint">
                      média de {(fh.area_ha / Math.max(fh.n_fires, 1)).toFixed(2)} ha por fogo
                    </div>
                  </Stat>
                  <Stat label="Pior ano">
                    <span className="font-display text-2xl font-bold">{fh.worst_year}</span>
                  </Stat>
                  <Stat label="Mês de pico">
                    <span className="font-display text-2xl font-bold">
                      {monthName(fh.peak_month)}
                    </span>
                  </Stat>
                </>
              ) : (
                <p className="text-sm text-mist-faint">Sem histórico disponível.</p>
              )}
              <div className="sm:col-span-2">
                <Stat label="Últimos 7 dias">
                  {live?.fires_7d && live.fires_7d.count > 0 ? (
                    <>
                      <span className="font-semibold">{live.fires_7d.count}</span> fogo
                      {live.fires_7d.count === 1 ? '' : 's'} registado
                      {live.fires_7d.count === 1 ? '' : 's'}
                      <ul className="mt-2 space-y-1 font-mono text-xs text-mist-faint">
                        {live.fires_7d.items.map((fi, i) => (
                          <li key={i}>
                            {fi.start_datetime?.slice(0, 16)}
                            {fi.burned_area_ha != null &&
                              fi.burned_area_ha >= 0.05 &&
                              ` · ${fi.burned_area_ha.toFixed(1)} ha`}
                            {fi.source && ` · ${fi.source}`}
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    'Sem fogos registados no concelho nos últimos 7 dias.'
                  )}
                </Stat>
              </div>
            </div>
          )}

          {tab === 'replay' && replay && (
            <ReplayPanel doc={replay} concelho={concelho} />
          )}
        </div>

        <p className="mt-10 text-xs leading-relaxed text-mist-faint">
          {DISCLAIMER}{' '}
          <Link to="/ignis/validacao" className="text-ember hover:brightness-110">
            Validação pública
          </Link>
          .
        </p>
      </div>
    </div>
  );
}

export default function Municipio() {
  const { slug } = useParams<{ slug: string }>();
  const { zones, loading, isAdmin } = useMunicipioZones();
  const [live, setLive] = useState<LiveDoc | null>(null);
  const [statics, setStatics] = useState<Concelho[] | null>(null);
  const [error, setError] = useState(false);

  const concelho = statics?.find((c) => c.slug === slug);

  useDocumentMeta(
    concelho
      ? `${concelho.nome} · consola municipal (acesso restrito) | Tutela IGNIS`
      : 'Consola municipal (acesso restrito) | Tutela IGNIS',
    'Consola operacional de risco de incêndio por concelho e freguesia para GTF e proteção civil municipal. Acesso reservado a entidades autorizadas.',
    undefined, // gated: no canonical, noindex
    { noindex: true },
  );

  useEffect(() => {
    let cancelled = false;
    Promise.all([loadLiveFresh(), loadConcelhos()])
      .then(([l, s]) => {
        if (cancelled) return;
        setLive(l);
        setStatics(s.concelhos);
      })
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading || (!live && !error)) return <Spinner />;
  if (error || !live || !statics) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-20 text-mist-muted">
        Não foi possível carregar os dados. Tente novamente dentro de momentos.
      </div>
    );
  }
  if (!zones || zones.length === 0) return <Restricted />;

  const allowed = isAdmin || (slug ? zones.includes(slug) : false);
  if (!slug || !concelho || !allowed) {
    const first = statics.find((c) => c.slug === zones[0]);
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-ink-950 text-mist">
        <div className="mx-auto max-w-3xl px-6 py-20">
          <h1 className="font-display text-2xl font-bold">Concelho indisponível</h1>
          <p className="mt-3 text-mist-muted">
            {slug && concelho
              ? `A sua conta não tem acesso a ${concelho.nome}.`
              : 'Escolha um dos concelhos atribuídos à sua conta.'}
          </p>
          {first && (
            <Link
              to={`/ignis/municipio/${first.slug}`}
              className="mt-6 inline-block rounded-lg bg-ember px-5 py-2.5 font-semibold text-ink-950 hover:brightness-110"
            >
              Abrir {first.nome}
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <Console
      concelho={concelho}
      live={live.concelhos.find((c) => c.slug === slug)}
      liveDoc={live}
      isAdmin={isAdmin}
      allConcelhos={statics}
      zones={zones}
    />
  );
}
